import React, { useState } from "react";
import { IconTrash } from "@tabler/icons-react";
import Modal from "../../components/Modal/Modal";
import { useClinicData } from "../../context/ClinicDataContext";
import { formatDate, formatDateShort, formatTime } from "../../utils/format";

export const LEAVE_TYPES = ["Annual leave", "Medical leave", "Training", "Public holiday", "Other"];

export const formatLeaveRange = (l) => (l.from === l.to ? formatDate(l.from) : `${formatDateShort(l.from)} – ${formatDate(l.to)}`);

// Days off for one dentist. Booking online and at the front desk is blocked on these dates.
const LeaveModal = ({ dentist, onClose }) => {
  const { today, addLeave, removeLeave, openAppointmentsFor, patientById } = useClinicData();
  const [form, setForm] = useState({ from: today, to: today, type: LEAVE_TYPES[0], note: "" });
  const [error, setError] = useState("");

  const upcoming = dentist.leave.filter((l) => l.to >= today);
  const clashes = form.from && form.to && form.to >= form.from ? openAppointmentsFor(dentist.id, form.from, form.to) : [];

  const set = (field) => (e) => {
    const value = e.target.value;
    setForm((f) => {
      const next = { ...f, [field]: value };
      if (field === "from" && next.to < value) next.to = value;
      return next;
    });
    setError("");
  };

  const handleAdd = () => {
    if (!form.from || !form.to) {
      setError("Choose the first and last day of leave.");
      return;
    }
    if (form.to < form.from) {
      setError("The last day can't be before the first day.");
      return;
    }
    const overlap = dentist.leave.find((l) => form.from <= l.to && form.to >= l.from);
    if (overlap) {
      setError(`This overlaps existing leave (${formatLeaveRange(overlap)}).`);
      return;
    }
    addLeave(dentist.id, { ...form, note: form.note.trim() });
    setForm({ from: today, to: today, type: LEAVE_TYPES[0], note: "" });
  };

  return (
    <Modal
      title={`Schedule & leave · ${dentist.name}`}
      onClose={onClose}
      width={620}
      footer={
        <button className="btn btn--primary" type="button" onClick={onClose}>
          Done
        </button>
      }
    >
      <section className="leave">
        <h3 className="leave__title">Upcoming leave</h3>
        {upcoming.length ? (
          <ul className="leave__list">
            {upcoming.map((l) => (
              <li className="leave__item" key={l.id}>
                <span>
                  <strong>{formatLeaveRange(l)}</strong>
                  <span>
                    {l.type}
                    {l.note ? ` · ${l.note}` : ""}
                  </span>
                </span>
                <button
                  className="btn btn--danger btn--sm"
                  type="button"
                  aria-label={`Remove leave ${formatLeaveRange(l)}`}
                  onClick={() => removeLeave(dentist.id, l.id)}
                >
                  <IconTrash size={16} stroke={1.75} />
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="leave__empty">No leave booked.</p>
        )}

        <h3 className="leave__title">Add leave</h3>
        <div className="form-grid">
          <label className="form-field">
            <span className="form-field__label">First day</span>
            <input className="form-field__control" type="date" min={today} value={form.from} onChange={set("from")} />
          </label>
          <label className="form-field">
            <span className="form-field__label">Last day</span>
            <input className="form-field__control" type="date" min={form.from || today} value={form.to} onChange={set("to")} />
          </label>
          <label className="form-field">
            <span className="form-field__label">Type</span>
            <select className="form-field__control" value={form.type} onChange={set("type")}>
              {LEAVE_TYPES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </label>
          <label className="form-field">
            <span className="form-field__label">Note (optional)</span>
            <input className="form-field__control" value={form.note} onChange={set("note")} placeholder="e.g. Conference in KL" />
          </label>
        </div>

        {clashes.length > 0 && (
          <div className="appt-form__notice leave__clash">
            {clashes.length} appointment{clashes.length > 1 ? "s" : ""} already booked in this period. Reschedule
            {clashes.length > 1 ? " them" : " it"} after adding the leave:
            <ul>
              {clashes.slice(0, 5).map((a) => (
                <li key={a.id}>
                  {formatDateShort(a.date)}, {formatTime(a.start)} · {patientById[a.patientId]?.name}
                </li>
              ))}
              {clashes.length > 5 && <li>and {clashes.length - 5} more</li>}
            </ul>
          </div>
        )}

        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <button className="btn btn--secondary leave__add" type="button" onClick={handleAdd}>
          Add leave
        </button>
      </section>
    </Modal>
  );
};

export default LeaveModal;
